/**
 * Tiny SVG chart engine. Designed for a few use cases:
 *
 *   - line / step charts over an x-axis of either ISO dates or ISO datetimes
 *   - dashed series for forecasts
 *   - multiple toggleable series sharing the same y-axis
 *   - vertical "now" marker
 *
 * Deliberately no dependency on uplot / d3 / recharts. The whole module is
 * <300 lines of helpers; the React renderer that consumes it lives in
 * components/LineChart.tsx.
 */

export interface SeriesPoint {
  /** x-axis position as an ISO string (date or datetime). null x is skipped. */
  x: string | null;
  /** y value; null becomes a gap in the line. */
  y: number | null;
  /**
   * Optional per-point metadata surfaced in the tooltip. Used for the
   * futures-history chart to show which physical contract a rolling-front
   * sample belonged to at that historical date (e.g. "Jul-2021" for an
   * old M1 point that today resolves to "Jul-2026").
   */
  meta?: string;
}

export interface Series {
  id: string;
  label: string;
  color: string;
  dashed?: boolean;
  step?: boolean; // step-after rendering, used for Base/Peak daily lines
  points: SeriesPoint[];
}

export interface Domain {
  xMin: number; // ms epoch
  xMax: number;
  yMin: number;
  yMax: number;
}

const DEFAULT_PAD = 0.04; // 4 % headroom on the y axis

/**
 * Compute the union x/y domain across all visible series. Returns null when
 * every series is empty (no points or all gaps).
 */
export function computeDomain(seriesList: readonly Series[]): Domain | null {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;
  for (const s of seriesList) {
    for (const p of s.points) {
      if (p.x === null) continue;
      const t = Date.parse(p.x);
      if (!Number.isFinite(t)) continue;
      if (t < xMin) xMin = t;
      if (t > xMax) xMax = t;
      if (p.y === null || !Number.isFinite(p.y)) continue;
      if (p.y < yMin) yMin = p.y;
      if (p.y > yMax) yMax = p.y;
    }
  }
  if (!Number.isFinite(xMin) || !Number.isFinite(yMin)) return null;
  if (xMin === xMax) {
    xMax = xMin + 24 * 3600 * 1000; // 1 day fallback width
  }
  if (yMin === yMax) {
    const eps = Math.max(1, Math.abs(yMin) * 0.05);
    yMin -= eps;
    yMax += eps;
  } else {
    const span = yMax - yMin;
    yMin -= span * DEFAULT_PAD;
    yMax += span * DEFAULT_PAD;
  }
  return { xMin, xMax, yMin, yMax };
}

export interface ChartGeometry {
  width: number;
  height: number;
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  innerWidth: number;
  innerHeight: number;
  domain: Domain;
}

export function makeGeometry(
  width: number,
  height: number,
  domain: Domain,
  paddings: { top?: number; right?: number; bottom?: number; left?: number } = {},
): ChartGeometry {
  const paddingTop = paddings.top ?? 12;
  const paddingRight = paddings.right ?? 14;
  const paddingBottom = paddings.bottom ?? 28;
  const paddingLeft = paddings.left ?? 44;
  return {
    width,
    height,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    innerWidth: Math.max(0, width - paddingLeft - paddingRight),
    innerHeight: Math.max(0, height - paddingTop - paddingBottom),
    domain,
  };
}

export function xScale(geo: ChartGeometry, msEpoch: number): number {
  const { domain, innerWidth, paddingLeft } = geo;
  if (domain.xMax === domain.xMin) return paddingLeft;
  return paddingLeft + ((msEpoch - domain.xMin) / (domain.xMax - domain.xMin)) * innerWidth;
}

export function yScale(geo: ChartGeometry, value: number): number {
  const { domain, innerHeight, paddingTop } = geo;
  if (domain.yMax === domain.yMin) return paddingTop + innerHeight / 2;
  return (
    paddingTop + (1 - (value - domain.yMin) / (domain.yMax - domain.yMin)) * innerHeight
  );
}

/**
 * Build an SVG path string from a series. null y values split the line into
 * segments so we render gaps rather than zero-line zigzags.
 */
export function buildPath(geo: ChartGeometry, series: Series): string {
  let d = "";
  let penDown = false;
  let prevX: number | null = null;
  let prevY: number | null = null;
  for (const p of series.points) {
    if (p.x === null || p.y === null || !Number.isFinite(p.y)) {
      penDown = false;
      continue;
    }
    const t = Date.parse(p.x);
    if (!Number.isFinite(t)) {
      penDown = false;
      continue;
    }
    const x = xScale(geo, t);
    const y = yScale(geo, p.y);
    if (!penDown) {
      d += `M ${round(x)} ${round(y)}`;
      penDown = true;
    } else if (series.step) {
      // Step-after: horizontal to new x at previous y, then vertical to new y.
      d += ` L ${round(x)} ${round(prevY ?? y)} L ${round(x)} ${round(y)}`;
    } else {
      d += ` L ${round(x)} ${round(y)}`;
    }
    prevX = x;
    prevY = y;
  }
  void prevX; // silence unused
  return d;
}

function round(n: number): string {
  return (Math.round(n * 100) / 100).toString();
}

// ---- Tick generation ------------------------------------------------------

export interface Tick {
  position: number;
  label: string;
}

const Y_TICK_COUNT = 5;

export function yTicks(domain: Domain, formatter: (v: number) => string): Tick[] {
  const step = niceStep((domain.yMax - domain.yMin) / Y_TICK_COUNT);
  const start = Math.ceil(domain.yMin / step) * step;
  const out: Tick[] = [];
  for (let v = start; v <= domain.yMax + 1e-9; v += step) {
    out.push({ position: v, label: formatter(v) });
  }
  return out;
}

function niceStep(rough: number): number {
  if (!Number.isFinite(rough) || rough <= 0) return 1;
  const exp = Math.floor(Math.log10(rough));
  const mag = Math.pow(10, exp);
  const norm = rough / mag;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * mag;
}

/**
 * Date ticks for the x-axis. Picks a sensible granularity based on the visible
 * window (hours / days / weeks). Labels formatted in de-DE / Europe/Berlin.
 */
export function xDateTicks(domain: Domain, maxTicks = 6): Tick[] {
  const span = domain.xMax - domain.xMin;
  const dayMs = 24 * 3600 * 1000;
  if (span <= 2 * dayMs) {
    // intraday: hours
    return hourTicks(domain, Math.min(maxTicks, 6));
  }
  if (span <= 14 * dayMs) {
    return dayTicks(domain, maxTicks);
  }
  return dayTicks(domain, maxTicks, true);
}

function hourTicks(domain: Domain, maxTicks: number): Tick[] {
  const hourMs = 3600 * 1000;
  const span = domain.xMax - domain.xMin;
  const stepHours = Math.max(1, Math.round(span / hourMs / maxTicks));
  const out: Tick[] = [];
  // Snap to hour boundary in Europe/Berlin by using a Date constructed in
  // local time then read back via formatter.
  let t = ceilToHour(domain.xMin);
  while (t <= domain.xMax) {
    out.push({ position: t, label: fmtBerlinHour(new Date(t)) });
    t += stepHours * hourMs;
  }
  return out;
}

function dayTicks(domain: Domain, maxTicks: number, longSpan = false): Tick[] {
  const dayMs = 24 * 3600 * 1000;
  const span = domain.xMax - domain.xMin;
  const stepDays = Math.max(1, Math.round(span / dayMs / maxTicks));
  let t = ceilToDay(domain.xMin);
  const out: Tick[] = [];
  while (t <= domain.xMax) {
    out.push({ position: t, label: fmtBerlinDay(new Date(t), longSpan) });
    t += stepDays * dayMs;
  }
  return out;
}

function ceilToHour(t: number): number {
  const d = new Date(t);
  d.setUTCMinutes(0, 0, 0);
  if (d.getTime() < t) d.setUTCHours(d.getUTCHours() + 1);
  return d.getTime();
}

function ceilToDay(t: number): number {
  const d = new Date(t);
  d.setUTCHours(0, 0, 0, 0);
  if (d.getTime() < t) d.setUTCDate(d.getUTCDate() + 1);
  return d.getTime();
}

function fmtBerlinHour(d: Date): string {
  return d.toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
}

function fmtBerlinDay(d: Date, withMonth: boolean): string {
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: withMonth ? "short" : "2-digit",
    timeZone: "Europe/Berlin",
  });
}

// ---- Default palette (8 distinguishable hues) -----------------------------

export const DEFAULT_PALETTE = [
  "#38bdf8", // sky-400
  "#a78bfa", // violet-400
  "#f59e0b", // amber-500
  "#34d399", // emerald-400
  "#f472b6", // pink-400
  "#facc15", // yellow-400
  "#fb7185", // rose-400
  "#22d3ee", // cyan-400
  "#fb923c", // orange-400
  "#a3e635", // lime-400
  "#60a5fa", // blue-400
  "#c084fc", // purple-400
  "#4ade80", // green-400
  "#e879f9", // fuchsia-400
  "#fde047", // yellow-300
  "#94a3b8", // slate-400
  "#fca5a5", // red-300
];

export function pickColor(idx: number): string {
  return DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
}

// ---- Europe/Berlin timezone helpers ---------------------------------------

/**
 * Build an ISO timestamp for a (date, hours, minutes) tuple anchored at the
 * matching Europe/Berlin wall-clock time. Handles CET / CEST correctly via
 * one Intl probe.
 *
 * The two-step technique mirrors the one in src/chart-api.ts (worker side)
 * but for the browser. Using Date.UTC + offset is unaffected by the user's
 * local timezone, so the same chart looks identical regardless of where the
 * viewer's machine is.
 */
export function berlinIso(date: string, hours = 0, minutes = 0): string {
  // Be defensive: callers may pass a full ISO string accidentally, or a
  // malformed date. Slice to the yyyy-MM-dd prefix and bail out cleanly if
  // we still can't make sense of it. Returning the raw input prevents
  // Intl.DateTimeFormat.formatToParts from throwing "Invalid time value"
  // when the chart re-renders mid-toggle.
  const ymd = (date ?? "").slice(0, 10);
  const [y, m, d] = ymd.split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return date ?? "";
  }
  // First guess: assume CET (+1).
  let guess = new Date(Date.UTC(y, m - 1, d, hours - 1, minutes));
  if (!Number.isFinite(guess.getTime())) return date ?? "";
  let offset = berlinOffsetMinutes(guess);
  if (offset !== 60) {
    // Adjust by the discovered offset.
    guess = new Date(Date.UTC(y, m - 1, d, hours, minutes) - offset * 60_000);
    // Verify one more time across DST boundary.
    const offset2 = berlinOffsetMinutes(guess);
    if (offset2 !== offset) guess = new Date(guess.getTime() + (offset - offset2) * 60_000);
  }
  return guess.toISOString();
}

function berlinOffsetMinutes(d: Date): number {
  if (!Number.isFinite(d.getTime())) return 60; // safe CET fallback
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const lookup: Record<string, string> = {};
    for (const p of parts) if (p.type !== "literal") lookup[p.type] = p.value;
    const asUtc = Date.UTC(
      Number(lookup.year),
      Number(lookup.month) - 1,
      Number(lookup.day),
      Number(lookup.hour === "24" ? "00" : lookup.hour),
      Number(lookup.minute),
      Number(lookup.second),
    );
    return Math.round((asUtc - d.getTime()) / 60_000);
  } catch {
    return 60;
  }
}
